import React, { useState, useEffect } from 'react';
import api from '../axios';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';

function Mt5Login({ isOpen, onClose, onSuccess, initialCredentials = {} }) {
  const [formData, setFormData] = useState({
    mt5_login: '',
    mt5_password: '',
    mt5_server: '',
    broker_name: '',
    account_type: 'demo'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const renderAsModalContent = typeof isOpen === 'boolean';
  const shouldRender = renderAsModalContent ? isOpen : true;

  useEffect(() => {
    if (shouldRender) {
      setFormData({
        mt5_login: initialCredentials.login || '',
        mt5_password: initialCredentials.password || '',
        mt5_server: initialCredentials.server || '',
        broker_name: initialCredentials.broker_name || '',
        account_type: initialCredentials.account_type || 'demo'
      });
      setError('');
    }
  }, [shouldRender, initialCredentials]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const { mt5_login, mt5_password, mt5_server, broker_name, account_type } = formData;

    if (!mt5_login || !mt5_password || !mt5_server) {
      setError('Please fill in all required fields.');
      setIsLoading(false);
      return;
    }

    if (isNaN(mt5_login) || mt5_login <= 0) {
      setError('MT5 login must be a positive number.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/mt5/login', {
        mt5_login: mt5_login.trim(),
        mt5_password: mt5_password,
        mt5_server: mt5_server.trim(),
        broker_name: broker_name.trim() || 'Default Broker',
        account_type: account_type || 'demo'
      });

      if (response.status >= 400) {
        const apiError = new Error(response.data?.message || 'Failed to connect to MT5');
        apiError.response = response;
        throw apiError;
      }

      if (response.data && response.data.success) {
        const connectionData = {
          mt5_login: mt5_login,
          mt5_password: mt5_password,
          mt5_server: mt5_server,
          broker_name: broker_name || 'Default Broker',
          account_type: account_type || 'demo',
          account_info: response.data.account_info || null,
          connected_at: new Date().toISOString()
        };

        if (onSuccess) {
          try {
            await onSuccess(connectionData);
          } catch (callbackError) {
            throw callbackError;
          }
        }

        if (onClose) {
          onClose();
        }
      } else {
        const apiError = new Error(response.data?.message || 'Failed to connect to MT5');
        apiError.response = response;
        throw apiError;
      }
    } catch (err) {
      let errorMessage = 'Failed to connect to MT5. Please check your credentials and try again.';

      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Invalid credentials. Please check your login details.';
        } else if (err.response.data?.message && err.response.data?.details) {
          errorMessage = `${err.response.data.message} ${err.response.data.details}`;
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data?.detail) {
          errorMessage = err.response.data.detail;
        }
      } else if (err.request) {
        errorMessage = 'No response from server. Please check your internet connection.';
      } else {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldRender) return null;

  const panel = (
    <div className="w-full max-w-xl overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(13,23,34,0.98),rgba(8,14,22,0.98))] shadow-[0_36px_90px_rgba(0,0,0,0.5)]">
      <div className="max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain sm:max-h-[calc(100vh-4rem)]">
      <div className="sticky top-0 z-10 border-b border-white/10 bg-[#0d1722] px-5 py-4 sm:px-7 sm:py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200/70">Broker connection</p>
            <h2 className="mt-2 text-2xl font-black tracking-tight text-white sm:text-3xl">Connect MT5 account</h2>
            <p className="mt-2 max-w-md text-sm leading-7 text-slate-400">Enter your MT5 login details to connect your account.</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-slate-300 transition hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="px-5 py-5 sm:px-7 sm:py-6">
        {error && (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-300/20 bg-rose-300/10 px-4 py-4 text-sm text-rose-100">
            <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="mb-6 grid gap-3 rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-4 text-sm text-slate-300 sm:grid-cols-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Required</div>
            <div className="mt-2 font-medium text-white">Login, password, server</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Account</div>
            <div className="mt-2 font-medium text-white">Demo or real</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Status</div>
            <div className="mt-2 font-medium text-white">Connection check</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="mt5_login" className="mb-2 block text-sm font-semibold text-slate-200">MT5 login number</label>
              <input
                id="mt5_login"
                name="mt5_login"
                type="text"
                value={formData.mt5_login}
                onChange={handleChange}
                className="block w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
                placeholder="e.g. 12345678"
                required
              />
            </div>

            <div>
              <label htmlFor="account_type" className="mb-2 block text-sm font-semibold text-slate-200">Account type</label>
              <select
                id="account_type"
                name="account_type"
                value={formData.account_type}
                onChange={handleChange}
                className="block w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
                required
              >
                <option value="demo">Demo account</option>
                <option value="real">Real account</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="mt5_password" className="mb-2 block text-sm font-semibold text-slate-200">MT5 password</label>
            <div className="relative">
              <input
                id="mt5_password"
                name="mt5_password"
                type={showPassword ? 'text' : 'password'}
                value={formData.mt5_password}
                onChange={handleChange}
                className="block w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 pr-12 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 transition hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="mt5_server" className="mb-2 block text-sm font-semibold text-slate-200">MT5 server</label>
              <input
                id="mt5_server"
                name="mt5_server"
                type="text"
                value={formData.mt5_server}
                onChange={handleChange}
                className="block w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
                placeholder="e.g. yourbroker-server"
                required
              />
            </div>

            <div>
              <label htmlFor="broker_name" className="mb-2 block text-sm font-semibold text-slate-200">Broker name</label>
              <input
                id="broker_name"
                name="broker_name"
                type="text"
                value={formData.broker_name}
                onChange={handleChange}
                className="block w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
                placeholder="Optional label"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
              >
                Cancel
              </button>
            ) : <span className="text-sm text-slate-500">Enter valid broker details to continue.</span>}

            <button
              type="submit"
              disabled={isLoading}
              className={`inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-5 py-3 text-sm font-bold text-slate-950 transition ${isLoading ? 'cursor-not-allowed opacity-70' : 'hover:scale-[1.02]'}`}
            >
              {isLoading ? (
                <>
                  <svg className="-ml-1 mr-3 h-5 w-5 animate-spin text-slate-950" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting...
                </>
              ) : 'Connect MT5 account'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );

  if (renderAsModalContent) {
    return panel;
  }

  return (
    <div className="min-h-screen bg-[#07111a] px-4 py-10 text-white">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.14),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.12),_transparent_28%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />
      <div className="mx-auto max-w-xl">{panel}</div>
    </div>
  );
}

export default Mt5Login;
